import React from 'react';
import { Document, Image, Page, Text, View } from '@react-pdf/renderer';
import { CountriesISO2, FONT, PAPER_SIZE } from '../../types/enums';
import { FontOption } from '../../types/badge';
import { registerFonts } from '../../badge/pdf/pdfDocument/pdfStyles';
import LabelValue from './labelValue/LabelValue';
import { getTranslations } from '../../translations';
import { PaymentMethods } from '../../common/enums';
import { getFontFamily } from '../../badge/pdf/utils';
import { FullWidthLabelValue } from './labelValue/FullWidthLabelValue';
import { receiptA4Styles } from './ReceiptPDFA4.styles';
import { useReceiptPDF } from './useReceiptPDF';
import { ProductWithBadges } from './productWithBadges/ProductWithBadges';
import { getReceiptStyles, getTaxTranslationsByCurrency } from './ReceiptPdf.utils';
import { MAX_LINES_FOR_ONE_PAGE } from './ReceiptPdf.constants';

export type ReceiptProps = {
  fonts?: FontOption[];
  products: {name: string; amount: number; badges: string[]; price: string}[];
  logo: string;
  payments: {paymentMethod: PaymentMethods; amount: string}[];
  orders: string[];
  authCodes: string[];
  date: string;
  discount?: string;
  tax: string;
  total: string;
  totalExclTax: string;
  companyName: string;
  locale: string;
  event?: string;
  size?: PAPER_SIZE.A4 | PAPER_SIZE.FOUR_X_TEN_INCH | PAPER_SIZE.W105xH140mm;
  currency: string;
  country: CountriesISO2;
};

export const ReceiptPDFA4: React.FC<ReceiptProps> = ({
  fonts = [],
  logo,
  products,
  payments,
  authCodes,
  tax,
  total,
  totalExclTax,
  date,
  orders,
  discount,
  companyName,
  locale,
  event,
  size = PAPER_SIZE.A4,
  currency,
  country
}) => {
  const {
    paymentMethods,
    ...receipt
  } = getTranslations(locale).receipt;
  const {taxLabel, exclTaxLabel} = getTaxTranslationsByCurrency(currency, country);
  const labelFontSizes = getReceiptStyles(size as ReceiptProps['size'], (products.length + payments.length + products.reduce((acc, product) => {
    return acc + product.badges.length;
  }, 0)) > MAX_LINES_FOR_ONE_PAGE);

  const {
    getPageSize,
    getPaymentsLabelStyles,
    getPaymentsInfoStyles,
    getMainWrapperStyles,
    getLogoStyles
  } = useReceiptPDF({ size });

  registerFonts(fonts);
  return (
    <Document>
      <Page size={getPageSize()}>
        <Image source={logo} style={getLogoStyles()} />
        <View style={getMainWrapperStyles()}>
          {(event && <Text
            style={[
              receiptA4Styles.event,
              getFontFamily(event, { bold: true, fallbackFont: FONT.HELVETICA_BOLD }),
              labelFontSizes.label,
              labelFontSizes.paddings,
              labelFontSizes.event
            ]}
          >{event.split('')}</Text>) || null}
          <View style={[receiptA4Styles.orderInfoBlock, labelFontSizes.paddings]}>
            <LabelValue
              labelSubtitleStyles={labelFontSizes.subLabel}
              labelStyles={labelFontSizes.label}
              label={receipt.order}
              value={orders.map(order => order).join(', ')}
            />
            <LabelValue
              labelSubtitleStyles={labelFontSizes.subLabel}
              labelStyles={labelFontSizes.label}
              label={receipt.authCode}
              value={authCodes.map(authCode => authCode).join(', ')}
            />
            <LabelValue
              labelSubtitleStyles={labelFontSizes.subLabel}
              labelStyles={labelFontSizes.label}
              label={receipt.date}
              value={date}
            />
          </View>
          <View style={labelFontSizes.totalWrapper}>
            <View
              style={[receiptA4Styles.companyInfo, labelFontSizes.margins]}
            >
              <LabelValue
                labelSubtitleStyles={labelFontSizes.subLabel}
                labelStyles={labelFontSizes.label}
                label={receipt.companyName}
                value={companyName}
              />
            </View>
            <View style={getPaymentsInfoStyles()}>
              {products.map((product) => (
                <ProductWithBadges {...product} styles={{
                  label: [labelFontSizes.label],
                  value: [labelFontSizes.subLabel]
                }} />
              ))}
              {discount && (<FullWidthLabelValue styles={{
                label: [labelFontSizes.label],
                value: [labelFontSizes.subLabel]
              }} fallbackFontFamily={FONT.HELVETICA_BOLD} label={receipt.discount} value={discount} />)}
              {totalExclTax && <FullWidthLabelValue styles={{
                label: [labelFontSizes.label],
                value: [labelFontSizes.subLabel]
              }} fallbackFontFamily={FONT.HELVETICA_BOLD} label={`${receipt.total} ${exclTaxLabel}`}
                                    value={totalExclTax} />}
              <FullWidthLabelValue styles={{
                label: [labelFontSizes.label],
                value: [labelFontSizes.subLabel]
              }} fallbackFontFamily={FONT.HELVETICA_BOLD} label={taxLabel} value={tax} />
              <FullWidthLabelValue styles={{
                label: [labelFontSizes.label],
                value: [labelFontSizes.subLabel]
              }} fallbackFontFamily={FONT.HELVETICA_BOLD} label={receipt.total} value={total} />
            </View>
            {payments.map(({paymentMethod, amount}, index) => (
              <FullWidthLabelValue
                boldLabel={false}
                styles={{
                  wrapper: [getPaymentsLabelStyles(), receiptA4Styles.paymentType],
                  label: [labelFontSizes.label],
                  value: [labelFontSizes.subLabel]
                }}
                label={`${receipt.payment} ${payments.length > 1 ? (`${index + 1} `) : ''}- ${paymentMethods[paymentMethod.toLowerCase()]}`}
                value={amount}
              />
            ))}
          </View>
          <View style={[receiptA4Styles.signatureContainer, labelFontSizes.signature.container]}>
            <Text style={[
              receiptA4Styles.signatureTitle,
              getFontFamily(receipt.signature, {bold: true, fallbackFont: FONT.HELVETICA_BOLD}),
              labelFontSizes.signature.title
            ]}>{receipt.signature}:</Text>
            <View style={[receiptA4Styles.signatureLine, labelFontSizes.signature.line]} />
          </View>
        </View>
      </Page>
    </Document>
  );
};
